import { Suspense, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useMatch, useNavigate } from 'react-router';

import { usePromoCodeContext } from 'contexts/PromoCodeContext/PromoCodeContext';
import { Avatar } from 'features/account/Avatar/Avatar';
import PerksFAQsModule from 'features/account/misfits-perks/PerksFAQsModule';
import { PlusMembershipFaq } from 'features/paid-membership';
import { AvatarModal } from 'features/paid-membership/AvatarModal/AvatarModal';
import { Divider } from 'honeydew/Divider';
import Loader from 'honeydew/Loader';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import useSmartBanner from 'hooks/useSmartBanner';
import { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useValidReferralsCampaign from 'hooks/useValidReferralsCampaign';
import AccountLinks from 'shared/AccountLinks';
import ClosedWindowOnboardingModal from 'shared/OnboardingModals/ClosedWindowOnboardingModal';
import ServiceDelayBanners from 'shared/ServiceDelayBanners';
import { isWithinOneWeekAfterAnniversary } from 'utils/dateUtils';

const AVATAR_MODAL_SHOWN_KEY = 'avatar_modal_shown';

function AccountLayout() {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const desktop = useMediaQuery({ minWidth: tailwindTheme?.screens?.md });
    const isPerksRoute = !!useMatch('/account/misfits-perks');
    const isMembershipRoute = !!useMatch('/account/membership');
    const isAccountHomeRoute = !!useMatch('/account');
    const isFoodPreferencesRoute = !!useMatch('/account/food-preferences');
    const navigate = useNavigate();
    const { addSmartbannerPadding } = useSmartBanner();
    const { customerPromoNanoBanner } = usePromoCodeContext();

    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

    const { membership } = useAccountPlusMembershipHook();

    const canShowAvatarModal = (() => {
        const hasBeenShown = localStorage.getItem(AVATAR_MODAL_SHOWN_KEY) === 'true';
        if (hasBeenShown) {
            return false;
        }

        const membershipStartDate = membership?.memberSinceDate;

        return isWithinOneWeekAfterAnniversary(membershipStartDate);
    })();

    // if there's a valid campaign, the nano banner at the top of the page will hide/show on scroll
    // and the padding on this layout will account for it
    const validReferralsCampaign = useValidReferralsCampaign();

    useEffect(() => {
        // add redirect from old upcoming deliveries page to new url
        if (window.location.pathname === '/account/delivery-schedule') navigate('/account/upcoming-deliveries');
    }, [navigate, window.location.pathname]);

    useEffect(() => {
        if (canShowAvatarModal) {
            setIsAvatarModalOpen(true);
        }
    }, [canShowAvatarModal]);

    const handleCloseAvatarModal = () => {
        setIsAvatarModalOpen(false);
        localStorage.setItem(AVATAR_MODAL_SHOWN_KEY, 'true');
    };

    const renderNanoBanner = validReferralsCampaign || customerPromoNanoBanner.showCustomerPromoNanoBanner;

    const getPaddingTop = () => {
        // Smart banner takes precedence
        if (addSmartbannerPadding) {
            return 'pt-[170px]';
        }

        // Desktop uses sticky navigation styles
        if (desktop) {
            return renderNanoBanner
                ? STICKY_SHOP_NAVIGATION_STYLES.paddingTopNanobanner
                : STICKY_SHOP_NAVIGATION_STYLES.paddingTopNoNanobanner;
        }

        if (window.isNativeApp) {
            return 'pt-[100px]';
        }

        return renderNanoBanner ? 'pt-[150px]' : 'pt-[110px]';
    };

    return (
        <main className={clsx('bg-mm-malt pb-40 if:bg-off-white', getPaddingTop())}>
            <div className="px-20 md:mx-auto md:flex md:max-w-[1140px] md:gap-20 lg:gap-80">
                {mobile ? (
                    <div className="mb-20">
                        <Avatar />
                    </div>
                ) : (
                    <div id="desktop-navigation-side-menu" className="min-w-[200px]">
                        <Avatar />
                        <Divider height={1} color="grey-light" />
                        <AccountLinks sideMenu />
                    </div>
                )}
                <div className="grow">
                    <Suspense fallback={<Loader />}>
                        {!isAccountHomeRoute && !isFoodPreferencesRoute && <ServiceDelayBanners className="mb-20" />}
                        <Outlet />
                    </Suspense>
                </div>
            </div>

            {!mobile && isPerksRoute && (
                <section className="mt-20 bg-white">
                    <div className="mx-auto w-[784px] px-25">
                        <PerksFAQsModule />
                    </div>
                </section>
            )}

            {isMembershipRoute && (
                <section className="mt-20 bg-white">
                    <div className="mx-auto max-w-[784px] px-25">
                        <PlusMembershipFaq />
                    </div>
                </section>
            )}

            {canShowAvatarModal && <AvatarModal open={isAvatarModalOpen} onClose={handleCloseAvatarModal} />}
            <ClosedWindowOnboardingModal />
        </main>
    );
}

export default AccountLayout;
